// Rückgewinnung (Daniel 01.11.2026): Regeln der persönlichen Seite /fuer-dich/<code>, ihrer Abmeldung und des Geschenks
// (10 Musiktaler, 14 Tage). Clara schreibt die Mails selbst über Gmail; die Plattform verschickt nichts. Datenbank:
// Migration `20261101230000_rueckgewinnung.sql`, Regeln in docs/regeln/admin-dashboard.md „Rückgewinnung“.
// Ohne schwere Importe, damit Vitest die Regeln prüfen kann (`rueckgewinnung.test.ts`).

/** Code aus der Datenbank: 10 Zeichen, Kleinbuchstaben und Ziffern (`rueckgewinnung_code_neu()`). */
const CODE = /^[a-z0-9]{10}$/;

/** Code aus der Adresse, klein geschrieben; ungültig `null` (die Seite fragt dann gar nicht erst). */
export function codeAusAdresse(roh: string | null | undefined): string | null {
  const code = String(roh ?? "").trim().toLowerCase();
  return CODE.test(code) ? code : null;
}

export const seitePfad = (code: string) => `/fuer-dich/${code}`;
export const abmeldePfad = (code: string) => `/fuer-dich/${code}/abmelden`;
/** Nach der Anmeldung zurück auf die Seite, die dann von selbst einlöst. */
export const einloesePfad = (code: string) => `/fuer-dich/${code}?einloesen=1`;
export const anmeldenZumEinloesen = (code: string) => `/login?redirect=${encodeURIComponent(einloesePfad(code))}`;

/**
 * Wohin der gemerkte Code nach der Anmeldung zurückführt (Daniel 02.11.2026). Anmelde- und Registrierungsseite leiten
 * nach `SIGNED_IN` selbst weiter (`?redirect=` bzw. `/mein-bereich`, mit 100 bis 300 ms Verzögerung). Sprang der
 * Rückweg schon dort los, las die Anmeldeseite danach die neue Adresse ohne `redirect` und schickte nach
 * `/mein-bereich`, das Geschenk war eingelöst, die Bestätigung weg. Deshalb greift der Rückweg nur dort, wo man nach
 * Anmeldung oder Bestätigungsmail landet: auf der Startseite und im Mitgliederbereich (gewohnte wie neue Ansicht).
 */
export function rueckwegZiel(pfad: string, code: string | null): string | null {
  if (!code) return null;
  const gelandet = pfad === "/" || pfad === "/mein-bereich" || pfad.startsWith("/mein-bereich/");
  return gelandet ? einloesePfad(code) : null;
}

/** Nach dem Abholen in den Mitgliederbereich; die neue Ansicht öffnet dort das Taler-Blatt (`TalerWaechter`). */
export const NACH_DEM_GESCHENK = "/mein-bereich?geschenk=taler";
export const geschenkHinweis = (suche: string) => new URLSearchParams(suche).get("geschenk") === "taler";
/** Die Adresse ohne den Hinweis (Neuladen öffnet das Blatt nicht noch einmal). */
export function ohneGeschenkHinweis(pfad: string, suche: string, anker: string): string {
  const params = new URLSearchParams(suche);
  params.delete("geschenk");
  const rest = params.toString();
  return `${pfad}${rest ? `?${rest}` : ""}${anker}`;
}

/** Ohne Konto: Registrierung (sie nimmt keinen Rückweg mit, deshalb der Merker unten). */
export const REGISTRIEREN = "/registrieren";
/** Unbekannter oder gesperrter Code: freundlich auf „kostenlos starten“. */
export const KOSTENLOS_STARTEN = "/kostenlos";

/**
 * Merker im Browser: Wer ohne Konto auf „Taler abholen“ tippt und sich erst registriert, landet nach der Bestätigung im
 * Mitgliederbereich. `RueckgewinnungNachAnmeldung` (in App.tsx) bringt die Person dann einmal zurück auf ihre Seite.
 */
export const MERKER = "mk_rueckgewinnung";
export const MERKER_TAGE = 14;

type Speicher = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function speicher(): Speicher | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function codeMerken(code: string, jetzt: Date = new Date(), s: Speicher | null = speicher()): void {
  try {
    s?.setItem(MERKER, JSON.stringify({ code, am: jetzt.toISOString() }));
  } catch {
    // privater Modus: dann bleibt nur der Rückweg über die Anmeldung
  }
}

export function gemerkterCode(jetzt: Date = new Date(), s: Speicher | null = speicher()): string | null {
  try {
    const roh = s?.getItem(MERKER);
    if (!roh) return null;
    const wert = JSON.parse(roh) as { code?: unknown; am?: unknown };
    const code = codeAusAdresse(typeof wert.code === "string" ? wert.code : null);
    const am = typeof wert.am === "string" ? new Date(wert.am).getTime() : NaN;
    if (!code || !Number.isFinite(am)) return null;
    if (jetzt.getTime() - am > MERKER_TAGE * 24 * 60 * 60 * 1000) return null;
    return code;
  } catch {
    return null;
  }
}

export function codeVergessen(s: Speicher | null = speicher()): void {
  try {
    s?.removeItem(MERKER);
  } catch {
    // nichts zu tun
  }
}

/** „15.11.2026“ in deutscher Zeit; der letzte Tag, an dem die Taler gelten. */
export function bisText(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() - 1);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });
}

export type Einloesung = { ok: boolean; schon?: boolean; taler?: number; bis?: string | null; grund?: string };

/** Der Satz nach dem Abholen, aus der Antwort von `rueckgewinnung_taler_einloesen()`. */
export function einloesenSatz(e: Einloesung, menge: number): string {
  if (e.ok) {
    const bis = bisText(e.bis);
    return `${e.taler ?? menge} Musiktaler gutgeschrieben, gültig bis ${bis}.`;
  }
  switch (e.grund) {
    case "schon_eingeloest":
      return "Das Geschenk zu dieser Seite wurde schon abgeholt.";
    case "konto_schon_beschenkt":
      return "Dein Konto hat dieses Geschenk schon einmal bekommen.";
    case "team":
      return "Für das Team gibt es keine Musiktaler.";
    case "nicht_angemeldet":
      return "Bitte melde dich zuerst an.";
    default:
      return "Diese Seite gibt es nicht mehr. Schreib uns gern an clara@musikonzept.de.";
  }
}

/** Video zum Reinschauen: nur, was die öffentliche Kursseite ohnehin zeigt (Trailer oder Lektion zum Reinschauen). */
export type Vorschau = { video: string; titel: string };
export type Empfehlung = {
  art: "kurs" | "lektion";
  titel: string;
  kurs: string;
  bild: string | null;
  slug: string | null;
  /** Claras Satz, warum es passt (`empfehlungen: [{id, grund}]`); bei der alten Form nur IDs fehlt er. */
  grund?: string | null;
  vorschau?: Vorschau | null;
};
/** Punkt im Block „Neu, seit du weg warst“: `icon` aus `NEU_SYMBOLE` oder ein `bild` aus unserem Speicher. */
export type NeuPunkt = { titel: string; text: string; icon?: string | null; bild?: string | null };
/** Miko zum Ausprobieren (Runde 2): Vorschläge, genutzte und erlaubte Antworten, schon gezeigte Beispiele. */
export type MikoBlock = { vorschlaege: string[]; genutzt: number; max: number; beispiele: string[] };
export const MIKO_LEER: MikoBlock = { vorschlaege: [], genutzt: 0, max: 2, beispiele: [] };

export type SeitenDaten =
  | { gefunden: false }
  | {
      gefunden: true;
      vorname: string | null;
      text: string | null;
      empfehlungen: Empfehlung[];
      taler_eingeloest: boolean;
      taler_bis: string | null;
      neu_titel: string;
      neu_punkte: NeuPunkt[];
      vimeo_id: string | null;
      taler_menge: number;
      taler_tage: number;
      miko?: MikoBlock;
    };

/** Clara schreibt mit Leerzeilen: je Absatz ein <p>, einzelne Umbrüche bleiben im Absatz. */
export function absaetze(text: string | null | undefined): string[] {
  return String(text ?? "")
    .split(/\n\s*\n/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Vimeo ohne Titel; `id` oder `id/hash` (Einstellungen, Vorschau einer Empfehlung). Von selbst nur mit `autoplay`. */
export function vimeoAdresse(angabe: string | null | undefined, autoplay = false): string | null {
  const treffer = /^\s*(\d{5,12})(?:\/([0-9a-z]{6,20}))?\s*$/i.exec(angabe ?? "");
  if (!treffer) return null;
  const [, id, hash] = treffer;
  return `https://player.vimeo.com/video/${id}?${hash ? `h=${hash}&` : ""}dnt=1&title=0&byline=0&portrait=0${autoplay ? "&autoplay=1" : ""}`;
}

// Abkürzungen, nach denen kein Satz endet („z. B.“, „ca.“); Zahlen mit Punkt („15. November“) auch nicht.
const ABKUERZUNG = /(?:^|[\s(])(?:[a-zäöü]|ca|bzw|usw|etc|ggf|evtl|inkl|max|min|nr|dr|bspw|vgl|std)\.$/i;

/**
 * Kopf der Seite (Runde 2): die ersten zwei Sätze aus Claras Text; der Rest steht weiter unten, je Absatz ein Eintrag.
 * Ohne Lookbehind (ältere iPhones), Satzende ist `.`, `!` oder `?` vor einem Leerzeichen oder dem Ende.
 */
export function kopfUndRest(text: string | null | undefined, saetze = 2): { kopf: string; rest: string[] } {
  const roh = String(text ?? "").trim();
  if (!roh) return { kopf: "", rest: [] };
  let gezaehlt = 0;
  let schnitt = -1;
  for (let i = 0; i < roh.length; i++) {
    const z = roh[i];
    if (z !== "." && z !== "!" && z !== "?") continue;
    let j = i + 1;
    while (j < roh.length && /[.!?“”"»)]/.test(roh[j])) j++;
    if (j < roh.length && !/\s/.test(roh[j])) continue;
    const davor = roh.slice(Math.max(0, i - 8), i + 1);
    if (z === "." && (/\d\.$/.test(davor) || ABKUERZUNG.test(davor))) continue;
    gezaehlt++;
    i = j - 1;
    if (gezaehlt === saetze) {
      schnitt = j;
      break;
    }
  }
  if (schnitt < 0 || !roh.slice(schnitt).trim()) return { kopf: roh.replace(/\s+/g, " "), rest: [] };
  return { kopf: roh.slice(0, schnitt).replace(/\s+/g, " ").trim(), rest: absaetze(roh.slice(schnitt)) };
}

/** Beginnt Claras Text klein („schön, dass …“), steht er wie ein Brief unter „Hallo Anna,“. */
export function begruessung(vorname: string | null | undefined, kopf: string): string {
  const hallo = vorname ? `Hallo ${vorname}` : "Hallo";
  return /^[a-zäöüß]/.test(kopf) ? `${hallo},` : hallo;
}

const ZAHLWORT = ["keine", "eine", "zwei", "drei", "vier", "fünf"];

/** „Noch zwei Fragen frei“ unter den Vorschlägen; ohne freie Fragen leer. */
export function freiSatz(frei: number): string {
  if (frei <= 0) return "";
  return frei === 1 ? "Noch eine Frage frei" : `Noch ${ZAHLWORT[frei] ?? frei} Fragen frei`;
}

/** Wie viele Antworten Miko hier zeigt, als Wort („zwei“). */
export const antwortenWort = (max: number) => ZAHLWORT[max] ?? String(max);

/** Die Symbole für „Neu, seit du weg warst“ (Schlüssel `icon`); unbekannt fällt auf „neu“. */
export const NEU_SYMBOLE = ["miko", "planen", "video", "tonart", "taler", "neu", "lieder", "webinar", "app", "kalender"] as const;
export type NeuSymbol = (typeof NEU_SYMBOLE)[number];
export function neuSymbol(icon: string | null | undefined, i: number): NeuSymbol {
  if (icon && (NEU_SYMBOLE as readonly string[]).includes(icon)) return icon as NeuSymbol;
  return (["miko", "taler", "neu", "lieder"] as const)[i % 4];
}

/** Bild zu einem Punkt nur aus der Bildwelt (`/bildwelt/…`) oder unserem öffentlichen Speicher, sonst keins. */
export function neuBild(bild: string | null | undefined): string | null {
  const b = String(bild ?? "").trim();
  if (/^\/bildwelt\/[a-z0-9/_.-]+\.(webp|png|jpe?g|svg)$/i.test(b)) return b;
  if (/^https:\/\/[a-z0-9]+\.supabase\.co\/storage\/v1\/object\/public\/[^\s"'<>]+$/i.test(b)) return b;
  return null;
}

/** Text unter einer Empfehlung: „Kurs“ oder „Lektion aus <Kurs>“. */
export function empfehlungArt(e: Empfehlung): string {
  return e.art === "kurs" ? "Kurs" : `Lektion aus „${e.kurs}“`;
}
